import React, { useEffect, useState } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import "../styles/PatientPrescriptions.css";
import { SERVER_URL } from "../config";

interface Prescription {
  id: string;
  prescriptionNumber: string;
  medication: {
    id: number;
    name: string;
    type: string;
  };
  dosage: string;
  strength: string;
  quantity: number;
  status: string;
  createdAt: string;
}

interface PatientPrescriptionsProps {
  patientId: string;
}

const PatientPrescriptions: React.FC<PatientPrescriptionsProps> = ({
  patientId,
}) => {
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrescriptions = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(
          `${SERVER_URL}/api/prescription/prescriptions?patientId=${patientId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch prescriptions.");
        }

        const data: Prescription[] = await response.json();
        setPrescriptions(data);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchPrescriptions();
  }, [patientId]);

  if (loading) return <p className="loading">Loading prescriptions...</p>;
  if (error) return <p className="error">{error}</p>;
  if (prescriptions.length === 0)
    return <p className="no-data">No prescriptions found for this patient.</p>;

  return (
    <div className="prescriptions-container">
      <h2>Prescriptions</h2>
      <table className="prescriptions-table">
        <thead>
          <tr>
            <th>Prescription Number</th>
            <th>Medication</th>
            <th>Dosage</th>
            <th>Strength</th>
            <th>Quantity</th>
            <th>Status</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {prescriptions.map((prescription) => (
            <tr key={prescription.id}>
              <td>{prescription.prescriptionNumber}</td>
              <td>{prescription.medication.name}</td>
              <td>{prescription.dosage}</td>
              <td>{prescription.strength}</td>
              <td>{prescription.quantity}</td>
              <td className={`status ${prescription.status.toLowerCase()}`}>
                {prescription.status}
              </td>
              <td>{new Date(prescription.createdAt).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PatientPrescriptions;
